<template>
  <div class="profile-matches">
    <div class="options">
      <input type="checkbox" id="showSetsChk" class="btn-check" v-model="showSetsRef"/>
      <label class="btn btn-outline-secondary btn-sm" for="showSetsChk">
        Show matching
      </label>
    </div>
    <div class="matches">
      <div v-if="hasOtherMatchesRef" class="instructions">
        These profiles are matches for this profile.
      </div>
      <div v-else class="instructions">
        No matches are defined for this profile.
      </div>
      <ul class="list-unstyled matches-list" :class="{ ['show-sets']: showSetsRef }">
        <li v-for="id in profileMatchIdsRef" :key="id.value" :class="{ current: showSetsRef && selectedIdRef == id.value }">
          <img v-if="showSetsRef && isSelectedSetMember(id)" src="@/assets/icons/check_gray_16.png" 
            class="member-check" :class="{ self: selectedIdRef == id.value }"/>
          <div class="item-card">
            <input type="radio" :id="id.value" class="btn-check" name="personSet" :value="id.value" v-model="selectedIdRef"/>
            <label :for="id.value">
              <ProfileCard 
                :person-id="id.value"
                show-profile-type
                :this-profile="isThisProfile(id)"
                :static="showSetsRef || isThisProfile(id)"
                :show-hover="showSetsRef || !isThisProfile(id)"
                :raised="showSelected(id.value)"/>
            </label>
            <div v-if="showSetsRef" class="item-actions">
              {{ getMatchCountText(getSelfMatchCount(id)) }}
              <button type="button" v-if="showEditAction(getSelfMatchSet(id))" class="btn btn-inline btn-link" @click="showSetModalForMatch(id)">
                Change
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
    <div v-if="showSetsRef" class="other-sets">
      <div class="instructions">
        <template v-if="otherSetsRef.length">
          These sources also define matching for this profile.
        </template>
        <template v-else>
          No other sources define matching for this profile.
        </template>
      </div>
      <ul v-if="otherSetsRef.length" class="list-unstyled show-sets">
        <li v-for="set in otherSetsRef" :key="set.id" :class="{ current: selectedIdRef == set.id }">
          <div class="item-card">
            <input type="radio" :id="set.id" class="btn-check" name="personSet" :value="set.id" v-model="selectedIdRef"/>
            <label :for="set.id">
              <ProfileCard v-if="hasOwnerGroupType(set, DataGroupType.Profile)" 
                :person-id="getOwnerGroup(set)?.startItemId" 
                show-profile-type
                static
                show-hover
                :raised="showSelected(set.id!)"/>
              <ResearchGroupItem v-if="hasOwnerGroupType(set, DataGroupType.Research)"
                :data-group-id="set.groupId"/>
              <SystemGroupItem v-if="hasOwnerGroupType(set, DataGroupType.System)"
                small
                show-hover
                :raised="showSelected(set.id!)"/>
            </label>
            <div class="item-actions">
              {{ getMatchCountText(getMatchCount(set)) }}
              <button type="button" v-if="showEditAction(set)" class="btn btn-inline btn-link" @click="showSetModal(set)">
                Change
              </button>
            </div>
          </div>
        </li>
      </ul>
      <div class="list-actions">
        <button type="button" class="btn btn-inline btn-link" data-bs-toggle="modal" data-bs-target="#addSetModal">
          Add a source
        </button>
      </div>
    </div>
    <div v-if="false" class="set-details">
      <span v-if="selectedSetRef">
        Match set last modified {{ DateTimeUtil.toUserActionDate(selectedSetRef?.modifiedDate) }} by 
        <UserLink :user-id="selectedSetRef?.modifiedUserId" dimmed></UserLink>
      </span>
      <span v-else-if="selectedIdRef">
        This profile does not define a match set
      </span>
      <span v-else>
        No match set selected
      </span>
    </div>
    <PersonSetModal ref="personSetModalRef" :person-id="personId"/>
    <Modal id="addSetModal" ok-close :ok-disabled="!canAddSetOwner()" @shown="addSetOwnerIdRef = undefined" @ok="addSet">
      <template #title>Add a source</template>
      <div>
        Select a profile to add as matching source:
      </div>
      <div>
        <ProfileSelector v-model="addSetOwnerIdRef"/>
        <div v-if="!canAddSetOwner()" class="invalid-feedback">
          {{ addSetOwnerErrorRef}}
        </div>
      </div>
      <template #okText>Add source</template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.profile-matches {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
}

.options {
  max-width: 500px;
  display: flex;
  align-items: center;
  gap: 1rem;

  label, button {
    min-width: 80px;
  }
}

h5 {
  margin-bottom: 0.25rem;
}

.instructions {
  font-size: 0.875rem;
  color: #888;
}

.matches,
.other-sets {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.matches > ul,
.other-sets > ul {
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & > li {
    margin-left: 1rem;
    display: grid;
    grid-template-columns: 0 auto;
    column-gap: 0;
    align-items: center;

    .member-check {
      grid-column: 1;
      align-self: start;
      margin-top: 12px;
      width: 16px;
      filter: brightness(0.7);

      &.self {
        opacity: 0.5;
      }
    }

    .item-card {
      grid-column: 2;
      width: max-content;
      display: flex; // HACK: avoid inline child layout
      flex-direction: column;
      gap: 0.25rem;

      .item-actions {
        font-size: 0.75rem;
        color: #888;

        .btn-link {
          margin-left: 0.25rem;
        }
      }
    }

    & > label {
      width: max-content;
      cursor: pointer;
    }
    &.current > .item-card > label > * {
      border-color: #eee;
      background: #f4f4f4;
    }
  }

  .match-placeholder > button {
    height: 48px;
    width: 400px;
    border: 1px dashed #ddd;
    padding-left: 1.5rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: #bbb;
  }

  &.show-sets {
    li {
      margin-left: 0.5rem;
      grid-template-columns: 16px auto;
      column-gap: 1rem;

      label {
        position: relative;
      }
    }
  }
}

.list-actions {
  min-height: 1.5rem;
  display: flex;
  gap: 0.5rem;
  font-size: 0.875rem;

  &.edit {
    padding: 1rem 0 0.5rem;

    .btn {
      min-width: 80px;
    }
  }
}

.other-sets {
  margin-top: 1rem;

  ul {
    width: 100%;
  }
}

.set-details {
  height: 2rem; // take up space even if no content
  font-size: 0.875rem;
  color: #aaa;
}

.profile-card {
  width: 400px;
}

.system-group-item {
  width: 400px;
}

.action {
  font-size: 0.875rem;
}

#addSetModal
{
  :deep(.modal-body) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0 1rem;
    min-height: 110px;
  }

  .invalid-feedback {
    display: block;
  }
}
</style>

<script lang="ts" setup>
import { computed, Ref, ref, watch } from 'vue'
import { DataGroup, DataGroupType } from '@/gp/GroupAdminModel'
import { useTokenManager } from '@/auth/TokenManager'
import { DateTimeUtil } from '@/util/LuxonUtil'
import { CompositeId } from '@/rd/CompositeId'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ProfileUtils } from './ProfileUtils'
import { isDefined } from '@/util/TypeScriptUtil'
import { LoadMode } from '@/util/AsyncData'
import { uniq } from 'lodash'
import { PersonSet } from '@/rd/ResearchDataModel'
import ProfileCard from '@/manage/ProfileCard.vue'
import ResearchGroupItem from '@/manage/researchgroups/ResearchGroupItem.vue'
import SystemGroupItem from '@/manage/SystemGroupItem.vue'
import UserLink from '@/manage/users/UserLink.vue'
import Modal from '@/util/Modal.vue'
import ProfileSelector from './ProfileSelector.vue'
import PersonSetModal from '../PersonSetModal.vue'
import { setEqual } from '@/util/IterableUtil'

const props = defineProps<{
  personId?: string,
}>()

const showSetsRef = ref(false)
const showRelSetsRef = ref(false)
const selectedIdRef = ref<string>()
const addedMemberIdsRef = ref([]) as Ref<CompositeId[]>
const addSetMemberIdRef = ref<string>()
const addSetOwnerIdRef = ref<string>()
const personSetModalRef = ref<InstanceType<typeof PersonSetModal>>()

const viewPersonStore = useViewPersonStore()
const dataGroupStore = useDataGroupStore()
const tokenManager = useTokenManager()

const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId, LoadMode.EnsureLoaded)?.data)
const personSetsRef = computed(() => viewPersonStore.getAsyncPersonSets(viewPersonRef.value?.id, LoadMode.EnsureLoaded)?.data ?? [])
const dataGroupsRef = computed(() => {
  const personGroupIds = viewPersonRef.value?.groupIds ?? []
  const setOwnerGroupIds = personSetsRef.value.map(set => set.groupId)
  const allGroupIds = uniq([...personGroupIds, ...setOwnerGroupIds])
  // load them all at once
  return dataGroupStore.getAsyncGroupList(allGroupIds, LoadMode.EnsureLoaded)
    .map(a => a.data).filter(isDefined)
})
const dataGroupsMapRef = computed(() => new Map(dataGroupsRef.value.map(dg => [dg.id!, dg])))

const profileMatchIdsRef = computed(() => ProfileUtils.getNonPlaceholderIds(viewPersonRef.value?.matchIds ?? [], dataGroupsRef.value)
  .map(id => CompositeId.parse(id)))

const otherSetsRef = computed(() => personSetsRef.value
  .filter(set => !isSelfMatchSet(set) && getMatchCount(set) > 1)
  .sort(compareSet))

const hasOtherMatchesRef = computed(() => profileMatchIdsRef.value.some(id => !isThisProfile(id)))
const canShowSetsRef = computed(() => personSetsRef.value.length > 0)

const selectedSetRef = computed(() => {
  if (!selectedIdRef.value)
    return undefined

  return getSelfMatchSet(CompositeId.parse(selectedIdRef.value)) ?? 
    personSetsRef.value.find(set => set.id == selectedIdRef.value)
})

const selectedOwnerGroupRef = computed(() => {
  const selectedGroupId = CompositeId.getGroupId(selectedIdRef.value)
  return dataGroupsMapRef.value.get(selectedGroupId ?? '')
})

watch(() => props.personId, () => {
  // reset interactive state
  showSetsRef.value = false
  showRelSetsRef.value = false
  selectedIdRef.value = undefined
})

watch([profileMatchIdsRef, otherSetsRef], () => {
  if (!selectedIdRef.value) {
    // set a default selection (if possible)
    selectedIdRef.value = profileMatchIdsRef.value.find(id => getSelfMatchCount(id) > 1)?.value
      ?? otherSetsRef.value.at(0)?.id
  }
}, { immediate: true })

function compareSet(a: PersonSet, b: PersonSet) {
  const aGroup = dataGroupsMapRef.value.get(a.groupId!)
  const bGroup = dataGroupsMapRef.value.get(b.groupId!)
  if (!aGroup || !bGroup)
    return 0

  const isThisOwned = (dg: DataGroup) => CompositeId.hasGroupId(props.personId, dg.id!)
  const isSelfOwned = (dg: DataGroup) => dg.ownerId == tokenManager.current.userId
  const isSystemGroup = (dg: DataGroup) => dg.groupType == DataGroupType.System

  return +isThisOwned(bGroup) - +isThisOwned(aGroup) || // sets defined by this profile (first)
      +isSelfOwned(bGroup) - +isSelfOwned(aGroup) || // own groups (first)
      +isSystemGroup(aGroup) - +isSystemGroup(bGroup) || // system group (last)
      (a.id! == b.id! ? 0 : (a.id! < b.id! ? -1 : 1)) // others in arbitrary but stable order (by set ids)
}

function isSelfMatchSet(set: PersonSet) {
  // a "match owned" set is owned by a matching person AND includes the person as a member
  return profileMatchIdsRef.value.some(id => 
    CompositeId.hasGroupId(set.id, id.groupId) && set.memberIds.includes(id.value))
}

function getSelfMatchSet(personId: CompositeId) {
  // NOTE: a person id uniquely identifies a set for a set owner, because the same owner cannot include the
  // same person in more than one set
  return personSetsRef.value.find(set =>
    CompositeId.hasGroupId(set.id, personId.groupId) && set.memberIds.includes(personId.value))
}

function getSelfMatchCount(personId: CompositeId) {
  return getMatchCount(getSelfMatchSet(personId))
}

function getOwnerGroup(set: PersonSet) {
  return dataGroupsMapRef.value.get(set.groupId!)
}

function hasOwnerGroupType(set: PersonSet, type: DataGroupType) {
  return getOwnerGroup(set)?.groupType == type
}

function isSetOwner(groupId: string) {
  return personSetsRef.value.some(set => CompositeId.hasGroupId(set.id, groupId))
}

function canEditGroup(groupId: string) {
  return dataGroupsMapRef.value.get(groupId)?.ownerId == tokenManager.current.userId
}

function showEditAction(set: PersonSet | undefined) {
  return set && selectedSetRef.value == set && canEditGroup(set.groupId!)
}

function getMatchCount(set: PersonSet | undefined) {
  return set?.memberIds.filter(isProfileMatch).length ?? 0
}

function getMatchCountText(count: number) {
  return count > 1 ? `Defines ${count} matches` : 'Does not define any matches'
}

function isProfileMatch(memberId: string) {
  return profileMatchIdsRef.value.some(id => id.value == memberId)
}

function isSelectedSetMember(personId: CompositeId) {
  return selectedSetRef.value && selectedSetRef.value.memberIds.includes(personId.value) &&
    getMatchCount(selectedSetRef.value) > 1 // ignore singleton sets
}

function isThisProfile(personId: CompositeId) {
  return personId.value == props.personId
}

function showSelected(id: string) {
  return showSetsRef.value && selectedIdRef.value == id
}

function showSetModalForMatch(personId: CompositeId) {
  return showSetModal(getSelfMatchSet(personId), personId.value)
}

function showSetModal(set: PersonSet | undefined, ownerId?: string) {
  personSetModalRef.value?.open(set, ownerId)
}

function addSet() {
  showSetModal(undefined, addSetOwnerIdRef.value)
  addSetOwnerIdRef.value = undefined
}

const addSetOwnerErrorRef = computed(() => {
  if (addSetOwnerIdRef.value) {
    const ownerGroupId = CompositeId.getGroupId(addSetOwnerIdRef.value)
    const ownerGroup = dataGroupStore.getLoadedGroup(ownerGroupId ?? '')
    if (ownerGroup?.ownerId != tokenManager.current.userId)
      return 'Select a profile you manage.'
  }
  return undefined
})

function canAddSetOwner() {
  return addSetOwnerIdRef.value && !addSetOwnerErrorRef.value
}
</script>